<template>
  <Popover v-model:open="open">
    <PopoverTrigger
      class="hidden min-w-0 max-w-[420px] flex-1 items-center gap-2 overflow-hidden whitespace-nowrap rounded-md border bg-card px-3.5 py-[9px] text-start text-sm leading-tight text-subtle-foreground md:flex"
    >
      <Icon name="lucide:search" class="h-4 w-4 flex-none" />
      <span class="min-w-0 truncate">{{ t("common.searchPlaceholder") }}</span>
    </PopoverTrigger>
    <PopoverContent
      align="start"
      :side-offset="8"
      class="w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0"
    >
      <Command>
        <CommandInput :placeholder="t('common.searchPlaceholder')" />
        <CommandList>
          <CommandEmpty>{{ t("common.searchNoResults") }}</CommandEmpty>
          <CommandGroup
            v-for="(group, gi) in groups"
            :key="gi"
            :heading="group.heading || undefined"
          >
            <CommandItem
              v-for="item in group.items"
              :key="item.link"
              :value="t(`common.${item.label}`)"
              class="cursor-pointer"
              @select="go(item)"
            >
              <Icon :name="item.icon" class="me-2 h-4 w-4 flex-shrink-0" />
              <span>{{ t(`common.${item.label}`) }}</span>
              <template v-if="isLocked(item)">
                <Icon name="lucide:lock" class="ms-auto h-3 w-3 opacity-60" aria-hidden="true" />
                <span class="sr-only">{{ t("authModal.title") }}</span>
              </template>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>

<script setup lang="ts">
import { dashboardRoutes } from "@/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type NavItem = {
  label: string;
  icon: string;
  link?: string;
  requiresAuth?: boolean;
};

type NavRoute = NavItem & {
  submenu?: boolean;
  children?: NavItem[];
};

const { t } = useI18n();
const router = useRouter();
const supabaseUser = useSupabaseUser();
const { requireLogin } = useAuthGate();

const open = ref(false);

// Guest + route yêu cầu đăng nhập => hiển thị khóa (giống Sidebar).
const isLocked = (item: NavItem) => !supabaseUser.value && !!item.requiresAuth;

// Section heading tái dùng đúng key Sidebar dùng.
const headingKeyFor: Record<string, string> = {
  tools: "navTools",
  apiManagement: "navAccount",
};

// Làm phẳng dashboardRoutes thành các nhóm, mirror cách Sidebar hiển thị:
// leaf trước submenu đầu tiên -> nhóm không heading; 'tools' -> Tools;
// 'apiManagement' + các leaf phía sau -> Account.
const groups = computed(() => {
  const result: { heading: string | null; items: NavItem[] }[] = [];
  let current: { heading: string | null; items: NavItem[] } = {
    heading: null,
    items: [],
  };
  result.push(current);

  for (const route of dashboardRoutes as unknown as NavRoute[]) {
    if (route.submenu && route.children) {
      const key = headingKeyFor[route.label];
      current = {
        heading: key ? t(`common.${key}`) : null,
        items: [...route.children],
      };
      result.push(current);
    } else if (route.link) {
      current.items.push(route);
    }
  }

  return result.filter((group) => group.items.length > 0);
});

const go = (item: NavItem) => {
  open.value = false;
  if (isLocked(item)) {
    requireLogin();
    return;
  }
  if (item.link) router.push(item.link);
};
</script>

<style scoped></style>
